import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import CreateOrderModal from '../components/CreateOrderModal';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import {
  getOperatorBasePath,
  getPrivateCustomersLabel,
  isDistributorRole
} from '../utils/operator';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Interfaces para estadísticas comerciales
interface CommercialStats {
  totalCustomers: number;
  totalPrivateCustomers: number;
  totalOrders: number;
  totalSales: number;
  avgOrderValue: number;
  conversionRate: number;
  topCustomers: Array<{
    name: string;
    spent: number;
    orders: number;
  }>;
  recentOrders: Array<{
    id: string;
    customer: string;
    date: string;
    total: number;
    status: string;
  }>;
  monthlySales: Array<{
    month: string;
    sales: number;
    orders: number;
  }>;
}

const CommercialDashboard = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [stats, setStats] = useState<CommercialStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefillCustomer] = useState<any | undefined>(undefined);
  const isDistributor = isDistributorRole(auth.user?.role);
  const basePath = getOperatorBasePath(auth.user?.role);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const ordersRes = await api.orders.getOrders();
        const canLoadPrivateCustomers = auth.user?.role === 'commercial' || auth.user?.role === 'distributor';
        const privateRes = canLoadPrivateCustomers
          ? await api.users.getMyPrivateClients()
          : { success: true, data: [] as any[] };

        if (!ordersRes.success) {
          setError('Error al cargar las estadísticas');
          return;
        }

        const orders = ordersRes.data || [];
        const privateCustomers = privateRes.success ? (privateRes.data || []) : [];
        const recentOrders = orders
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 10)
          .map((o: any) => ({
            id: o._id,
            customer: o.dropShipping?.recipient?.name || o.customer?.name || o.customerInfo?.name || 'Cliente',
            date: o.createdAt,
            total: o.totalAmount || 0,
            status: o.status || 'pending'
          }));

        const monthlySalesMap = new Map<string, { month: string; sales: number; orders: number }>();
        orders.forEach((order: any) => {
          const month = new Date(order.createdAt).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });
          const current = monthlySalesMap.get(month) || { month, sales: 0, orders: 0 };
          current.sales += order.totalAmount || 0;
          current.orders += 1;
          monthlySalesMap.set(month, current);
        });

        const monthlySales = Array.from(monthlySalesMap.values()).slice(-6);
        const topCustomersMap = new Map<string, { name: string; spent: number; orders: number }>();
        orders.forEach((order: any) => {
          const key = order.dropShipping?.recipient?.name || order.customer?.name || order.customerInfo?.name || 'Cliente';
          const current = topCustomersMap.get(key) || { name: key, spent: 0, orders: 0 };
          current.spent += order.totalAmount || 0;
          current.orders += 1;
          topCustomersMap.set(key, current);
        });
        const topCustomers = Array.from(topCustomersMap.values())
          .sort((a, b) => b.spent - a.spent)
          .slice(0, 5);

        if (isDistributor) {
          setStats({
            totalCustomers: privateCustomers.filter((customer: any) => Boolean(customer.email)).length,
            totalPrivateCustomers: privateCustomers.length,
            totalOrders: orders.length,
            totalSales: orders.reduce((sum: number, order: any) => sum + (order.totalAmount || 0), 0),
            avgOrderValue: orders.length > 0
              ? orders.reduce((sum: number, order: any) => sum + (order.totalAmount || 0), 0) / orders.length
              : 0,
            conversionRate: 0,
            topCustomers,
            recentOrders,
            monthlySales
          });
          return;
        }

        const analyticsRes = await api.analytics.getCommercialAnalytics({ months: 6 });
        if (!analyticsRes.success || !analyticsRes.data) {
          setError('Error al cargar las estadísticas');
          return;
        }

        const analytics = analyticsRes.data;
        setStats({
          totalCustomers: analytics.totalCustomers || 0,
          totalPrivateCustomers: analytics.totalPrivateCustomers || privateCustomers.length,
          totalOrders: analytics.totalOrders || orders.length,
          totalSales: analytics.totalSales || 0,
          avgOrderValue: analytics.avgOrderValue || 0,
          conversionRate: (analytics.totalCustomers || 0) > 0 ? (topCustomers.length * 100) / (analytics.totalCustomers || 1) : 0,
          topCustomers: (analytics.topCustomers || []).slice(0, 5).map(c => ({
            name: c.name,
            spent: c.totalSpent,
            orders: c.orders
          })),
          recentOrders,
          monthlySales: analytics.monthlySales || monthlySales
        });
      } catch (err) {
        console.error('Error al cargar estadísticas del dashboard comercial:', err);
        setError('Error al cargar los datos. Inténtalo de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchStats();
  }, [isDistributor, auth.user?.role]);
  
  // Variantes para animaciones
  const cardVariants = {
    initial: { opacity: 0, y: 20 },
    animate: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: 0.1 * i
      }
    })
  };
  
  // Preparar datos para gráficas
  const prepareSalesChartData = () => {
    if (!stats?.monthlySales) return null;
    
    const labels = stats.monthlySales.map(month => month.month);
    const sales = stats.monthlySales.map(month => month.sales);
    const orders = stats.monthlySales.map(month => month.orders);
    
    return {
      labels,
      datasets: [
        {
          label: 'Ventas (€)',
          data: sales,
          borderColor: 'rgba(99, 102, 241, 1)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          fill: true,
          tension: 0.3
        },
        {
          label: 'Pedidos',
          data: orders,
          borderColor: 'rgba(79, 70, 229, 1)',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          tension: 0.3
        }
      ]
    };
  };
  
  const prepareTopCustomersChartData = () => {
    if (!stats?.topCustomers || stats.topCustomers.length === 0) return null;
    
    const labels = stats.topCustomers.map(customer => customer.name);
    const data = stats.topCustomers.map(customer => customer.spent);
    
    const backgroundColor = [
      'rgba(99, 102, 241, 0.7)',
      'rgba(79, 70, 229, 0.7)',
      'rgba(16, 185, 129, 0.7)',
      'rgba(245, 158, 11, 0.7)',
      'rgba(239, 68, 68, 0.7)'
    ];
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderColor: backgroundColor.map(color => color.replace('0.7', '1')),
          borderWidth: 1
        }
      ]
    };
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-lg"
      >
        <p>{error}</p>
      </motion.div>
    );
  }
  
  if (!stats) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <p className="text-gray-500">No hay datos disponibles</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Tarjetas de resumen */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div 
            custom={0} 
            variants={cardVariants} 
            initial="initial" 
            animate="animate"
            className="bg-white rounded-xl shadow-sm border border-gray-200"
          >
            <div className="p-6">
              <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">{isDistributor ? 'Con email logístico' : 'Clientes registrados'}</h3>
              <p className="text-2xl font-semibold mt-2">{stats.totalCustomers}</p>
              <p className="text-sm text-gray-600 mt-1">{isDistributor ? 'Destinatarios listos para notificación' : 'Clientes activos'}</p>
            </div>
          </motion.div>
          
          <motion.div 
            custom={1} 
            variants={cardVariants} 
            initial="initial" 
            animate="animate"
            className="bg-white rounded-xl shadow-sm border border-gray-200"
          >
            <div className="p-6">
              <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-green-50 text-green-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">{getPrivateCustomersLabel(auth.user?.role)}</h3>
              <p className="text-2xl font-semibold mt-2">{stats.totalPrivateCustomers}</p>
              <p className="text-sm text-gray-600 mt-1">{isDistributor ? 'Destinatarios guardados' : 'Clientes VIP'}</p>
            </div>
          </motion.div>
          
          <motion.div 
            custom={2} 
            variants={cardVariants} 
            initial="initial" 
            animate="animate"
            className="bg-white rounded-xl shadow-sm border border-gray-200"
          >
            <div className="p-6">
              <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-yellow-50 text-yellow-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">Pedidos</h3>
              <p className="text-2xl font-semibold mt-2">{stats.totalOrders}</p>
              <p className="text-sm text-gray-600 mt-1">Total de pedidos</p>
            </div>
          </motion.div>
          
          <motion.div 
            custom={3} 
            variants={cardVariants} 
            initial="initial" 
            animate="animate"
            className="bg-white rounded-xl shadow-sm border border-gray-200"
          >
            <div className="p-6">
              <div className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center bg-purple-50 text-purple-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">{isDistributor ? 'Facturación' : 'Ventas'}</h3>
              <p className="text-2xl font-semibold mt-2">{stats.totalSales.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
              <p className="text-sm text-gray-600 mt-1">Ticket medio: {stats.avgOrderValue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
            </div>
          </motion.div>
        </div>
        
        {/* Gráficas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfica de ventas mensuales */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{isDistributor ? 'Tendencia de pedidos' : 'Tendencia de ventas'}</h2>
            {prepareSalesChartData() && (
              <div className="h-64">
                <Line
                  data={prepareSalesChartData() || { labels: [], datasets: [] }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      tooltip: {
                        mode: 'index' as const,
                        intersect: false,
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>
            )}
          </motion.div>
          
          {/* Top clientes */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{isDistributor ? 'Top destinatarios por importe' : 'Top clientes por gasto'}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-64">
                {prepareTopCustomersChartData() && (
                  <Pie
                    data={prepareTopCustomersChartData() || { labels: [], datasets: [] }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'right' as const,
                        }
                      }
                    }}
                  />
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Ranking</h3>
                <div className="space-y-2">
                  {stats.topCustomers.map((customer, index) => (
                    <div key={customer.name} className="flex justify-between">
                      <span className="text-sm">{index + 1}. {customer.name}</span>
                      <span className="text-sm font-medium">{customer.spent.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Pedidos recientes */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Pedidos recientes</h2>
              <button 
                onClick={() => navigate(`${basePath}/orders`)}
                className="text-sm font-medium text-black hover:text-gray-700"
              >
                Ver todos →
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.recentOrders.length > 0 ? (
                  stats.recentOrders.slice(0, 10).map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        #{order.id.slice(-6)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.customer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.date).toLocaleDateString('es-ES')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'shipped' ? 'bg-indigo-100 text-indigo-800' :
                          order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {order.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button 
                          onClick={() => navigate(`${basePath}/orders/${order.id}`)}
                          className="text-black hover:text-gray-700 font-medium"
                        >
                          Ver detalles
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      No hay pedidos recientes
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
        
        {/* Acciones rápidas */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Acciones rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link 
              to={`${basePath}/private-customers`} 
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-lg bg-green-50 text-green-500 flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{isDistributor ? 'Nuevo cliente final' : 'Nuevo cliente privado'}</h3>
                  <p className="text-sm text-gray-600">{isDistributor ? 'Guardar un destinatario' : 'Agregar cliente VIP'}</p>
                </div>
              </div>
            </Link>
            
            <button 
              onClick={() => setModalOpen(true)}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Crear pedido</h3>
                  <p className="text-sm text-gray-600">Nuevo pedido manual</p>
                </div>
              </div>
            </button>
            
            {!isDistributor && (
              <Link 
                to="/commercial/customers" 
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-500 flex items-center justify-center mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Gestionar clientes</h3>
                    <p className="text-sm text-gray-600">Ver todos los clientes</p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </motion.div>
      </div>
      
      <CreateOrderModal open={modalOpen} onClose={() => setModalOpen(false)} defaultCustomer={prefillCustomer} />
    </>
  );
};

export default CommercialDashboard;
